import { Head, useAuth } from "zudoku/components";
import { useZudoku } from "zudoku/hooks";
import { useState, useEffect, useRef } from "react";

// ---------------------------------------------------------------------------
// PdfViewer - renders every PDF page to a <canvas> so the full document
// height is native page height (no internal iframe scroll).
// ---------------------------------------------------------------------------
const PDFJS_CDN = "https://cdn.jsdelivr.net/npm/pdfjs-dist@4.9.155/build/pdf.min.mjs";
const PDFJS_WORKER = "https://cdn.jsdelivr.net/npm/pdfjs-dist@4.9.155/build/pdf.worker.min.mjs";

interface PdfViewerProps {
  url: string;
}

const PdfViewer = ({ url }: PdfViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const renderPdf = async () => {
      setPdfLoading(true);
      setPdfError(null);

      try {
        // Dynamically load pdfjs from CDN
        const pdfjsLib: any = await import(/* @vite-ignore */ PDFJS_CDN);
        pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;

        const loadingTask = pdfjsLib.getDocument(url);
        const pdf = await loadingTask.promise;

        if (cancelled || !containerRef.current) return;

        // Clear any previously rendered pages
        containerRef.current.innerHTML = "";

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (cancelled) break;

          const page = await pdf.getPage(pageNum);
          const containerWidth = containerRef.current?.clientWidth || 800;

          // Scale so the page fills the container width
          const unscaledViewport = page.getViewport({ scale: 1 });
          const scale = containerWidth / unscaledViewport.width;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = "100%";
          canvas.style.display = "block";

          if (pageNum < pdf.numPages) {
            canvas.style.marginBottom = "8px";
          }

          containerRef.current?.appendChild(canvas);

          const ctx = canvas.getContext("2d")!;
          await page.render({ canvasContext: ctx, viewport }).promise;
        }
      } catch (err) {
        if (!cancelled) {
          console.error("PDF render error:", err);
          setPdfError("Failed to render PDF. Please try downloading it directly.");
        }
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    };

    renderPdf();
    return () => { cancelled = true; };
  }, [url]);

  return (
    <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {pdfLoading && (
        <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
          Rendering report…
        </div>
      )}

      {pdfError && (
        <div className="p-8 text-center text-red-600 dark:text-red-400 text-sm">
          {pdfError}
        </div>
      )}

      <div ref={containerRef} className="p-4" />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Quota warning helpers
// ---------------------------------------------------------------------------
interface QuotaWarningAction {
  text: string;
  href: string;
}

interface QuotaWarning {
  message: string;
  actions: QuotaWarningAction[];
}

/**
 * Returns a warning when the user is approaching (or has hit) the monthly
 * company-report limit. Returns null when no warning is needed.
 *
 * Thresholds: ≤ 20 % of limit remaining, with a floor of 2.
 */
const getQuotaWarning = (
  tier: string | null,
  role: string | null,
  quota: { used: number; limit: number; remaining: number }
): QuotaWarning | null => {
  const threshold = Math.max(2, Math.floor(quota.limit * 0.1));
  if (quota.remaining > threshold) return null;

  // API consumers cannot self-serve — direct them to their admin
  if (role === "api-consumer") {
    return {
      message:
        "You are approaching your company report limit. Please contact your administrator to increase your limit.",
      actions: [],
    };
  }

  const normalised = (tier ?? "").toLowerCase().replace(/[^a-z]/g, "-");

  switch (normalised) {
    case "data-sampler":
    case "data sampler":
      return {
        message: "You are approaching your company report limit.",
        actions: [
          { text: "Upgrade to Core tier", href: "/subscription-management" },
        ],
      };
    case "core":
      return {
        message: "You are approaching your company report limit.",
        actions: [
          { text: "Upgrade to Scale tier", href: "/subscription-management" },
        ],
      };
    case "scale":
      return {
        message: "You are approaching your company report limit.",
        actions: [
          { text: "Buy additional companies pack", href: "/addon-packs" },
          { text: "Upgrade to Enterprise", href: "/subscription-management" },
        ],
      };
    case "enterprise":
      return {
        message: "You are approaching your company report limit.",
        actions: [
          { text: "Buy additional companies pack", href: "/addon-packs" },
        ],
      };
    default:
      return {
        message: "You are approaching your company report limit. Please upgrade your plan for more access.",
        actions: [
          { text: "View Plans", href: "/subscription-management" },
        ],
      };
  }
};

export const ReportCustomPage = () => {
  const [ticker, setTicker] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportImage, setReportImage] = useState<string | null>(null);
  const [reportPdf, setReportPdf] = useState<string | null>(null);
  const [reportType, setReportType] = useState<'image' | 'pdf' | null>(null);
  const [fileFormat, setFileFormat] = useState<'pdf' | 'jpg'>('pdf');
  const [tickerQuota, setTickerQuota] = useState<{ used: number; limit: number; remaining: number } | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [userTier, setUserTier] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const auth = useAuth();
  const { authentication } = useZudoku();

  // Get a fresh, valid access token (handles expiry + refresh automatically)
  const getAccessToken = async (): Promise<string | null> => {
    try {
      if (authentication?.getAccessToken) {
        return await authentication.getAccessToken();
      }
    } catch (e) {
      console.warn("getAccessToken() failed, falling back:", e);
    }
    return (
      (auth as any).providerData?.accessToken ||
      (auth as any).providerData?.access_token ||
      null
    );
  };

  // Fetch ticker quota on component mount and when auth changes
  const fetchTickerQuota = async () => {
    if (!auth.isAuthenticated) {
      setTickerQuota(null);
      return;
    }

    setQuotaLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) {
        return;
      }

      // Call the API endpoint to get ticker quota
      const response = await fetch(
        "https://api.securitydatapoint.com/api/v1/tickers/quota",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Decode JWT once to extract tier + role for warning logic
      try {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(
          decodeURIComponent(
            atob(base64)
              .split("")
              .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
              .join("")
          )
        );
        const tier =
          payload["https://api.securitydatapoint.com/tier"] ||
          payload.tier ||
          null;
        const rawRoles =
          payload["https://api.securitydatapoint.com/roles"] ||
          payload.roles ||
          [];
        const rolesArr: string[] = Array.isArray(rawRoles)
          ? rawRoles
          : [rawRoles];
        const role = rolesArr.includes("superadmin")
          ? "superadmin"
          : rolesArr.includes("api-consumer")
          ? "api-consumer"
          : rolesArr[0] ?? null;
        setUserTier(tier);
        setUserRole(role);
      } catch (e) {
        console.error("Failed to decode token for tier/role:", e);
      }

      if (response.ok) {
        const quota = await response.json();
        setTickerQuota(quota);
      } else {
        // Get more details about the error
        const errorBody = await response.text();
        console.error("Failed to fetch ticker quota:", {
          status: response.status,
          statusText: response.statusText,
          body: errorBody
        });
      }
    } catch (err) {
      console.error("Error fetching ticker quota:", err);
    } finally {
      setQuotaLoading(false);
    }
  };

  // Fetch quota when auth state changes
  useEffect(() => {
    fetchTickerQuota();
  }, [auth.isAuthenticated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedTicker = ticker.trim().toUpperCase();
    
    if (!trimmedTicker) {
      setError("Please enter a company ticker");
      return;
    }

    // Validate ticker format (letters only, 1-5 characters, or SAMPLTCK1-SAMPLTCK10)
    if (!/^([A-Z]{1,5}|SAMPLTCK\d{1,2})$/.test(trimmedTicker)) {
      setError("Invalid ticker format. Please enter 1-5 letters (e.g., AACG, TYG, DNUT, GLPG)");
      return;
    }

    setError("");
    setLoading(true);
    setReportImage(null);
    setReportPdf(null);
    setReportType(null);

    try {
      // Check if user is authenticated
      if (!auth.isAuthenticated) {
        setError("Please log in to access reports");
        setLoading(false);
        return;
      }

      // Get a fresh access token (handles expiry + refresh)
      const token = await getAccessToken();
      if (!token) {
        setError("Unable to retrieve access token. Please log in again.");
        setLoading(false);
        return;
      }

      // Decode JWT to extract tier for the report endpoint query param
      let userTier = 'roles-unset';
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(decodeURIComponent(
          atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
        ));
        userTier =
          payload["https://api.securitydatapoint.com/tier"] ||
          payload.tier ||
          'roles-unset';
      } catch (e) {
        console.error("Failed to decode token:", e);
      }

      // Determine endpoint based on file format
      const endpoint = fileFormat === 'pdf'
        ? `https://api.securitydatapoint.com/api/v1/ticker/report/pdf?ticker=${trimmedTicker}&tier=${userTier}`
        : `https://api.securitydatapoint.com/api/v1/ticker/report/jpg?ticker=${trimmedTicker}&tier=${userTier}`;

      // Make authenticated API request to the report endpoint
      const response = await fetch(
        endpoint,
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          setError("Authentication failed. Please log in again.");
        } else if (response.status === 404) {
          setError(`Company report not found for ticker: ${trimmedTicker}`);
        } else if (response.status === 429) {
          // Check if it's a ticker limit error or a generic rate limit
          const errorText = await response.text();
          let parsedError: any = null;
          try {
            parsedError = JSON.parse(errorText);
          } catch (_) {}
          console.error("429 Rate limit response:", {
            status: response.status,
            statusText: response.statusText,
            parsed: parsedError,
            body: errorText,
            ticker: trimmedTicker,
            currentQuota: tickerQuota,
          });
          if (errorText.includes("ticker limit") || errorText.includes("Company Reports Available This Month")) {
            // Monthly ticker quota exhausted — refresh so UI reflects server-side state
            // (e.g. plan change from core→scale may shift the limit mid-month)
            await fetchTickerQuota();
            setError(`You have reached your company report limit for this month. Please upgrade your plan for more access.`);
          } else {
            // Generic rate limit (per-second / per-minute window) — not a quota issue
            const detail = parsedError?.title || parsedError?.detail || "Too Many Requests";
            setError(`Rate limit exceeded (${detail}). Please wait a moment and try again.`);
          }
        } else {
          setError(`Failed to fetch report: ${response.statusText}`);
        }
        setLoading(false);
        return;
      }

      // Check content type
      const contentType = response.headers.get("content-type");
      
      if (contentType?.includes("application/pdf")) {
        // Handle PDF response
        const blob = await response.blob();
        const pdfUrl = URL.createObjectURL(blob);
        setReportPdf(pdfUrl);
        setReportType('pdf');
      } else if (contentType?.includes("image/jpeg") || contentType?.includes("image/jpg") || contentType?.includes("image/png")) {
        // Handle image response
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setReportImage(imageUrl);
        setReportType('image');
      } else {
        setError(`Unsupported content type: ${contentType}`);
      }

      // Read quota from response headers (set by monthly-usage-check-outbound policy
      // on the same request that ran the limiter — always in sync, no extra round trip)
      const quotaUsed = response.headers.get("X-Quota-Used");
      const quotaLimit = response.headers.get("X-Quota-Limit");
      const quotaRemaining = response.headers.get("X-Quota-Remaining");
      if (quotaUsed !== null && quotaLimit !== null && quotaRemaining !== null) {
        setTickerQuota({
          used: parseInt(quotaUsed, 10),
          limit: parseInt(quotaLimit, 10),
          remaining: parseInt(quotaRemaining, 10),
        });
      } else {
        // Fallback: headers not present — fetch quota via dedicated endpoint
        await fetchTickerQuota();
      }
      
      setLoading(false);
    } catch (err) {
      setError(`Error: ${err instanceof Error ? err.message : "Unknown error occurred"}`);
      setLoading(false);
    }
  };

  const handleClear = () => {
    if (reportImage) {
      URL.revokeObjectURL(reportImage);
      setReportImage(null);
    }
    if (reportPdf) {
      URL.revokeObjectURL(reportPdf);
      setReportPdf(null);
    }
    setReportType(null);
  };

  return (
    <section className="max-w-4xl mx-auto px-4 py-12">
      <Head>
        <title>Company Report - SDP Developer Portal</title>
      </Head>
      
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Company Report</h1>
        
        {/* Ticker Quota Display */}
        {auth.isAuthenticated && tickerQuota && (
          <div className="mt-6 inline-flex items-center gap-3 px-6 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="text-sm">
              <span className="font-semibold text-blue-900 dark:text-blue-100">
                Company Reports Available This Month:
              </span>
              <span className="ml-2 text-blue-700 dark:text-blue-300">
                {tickerQuota.remaining} of {tickerQuota.limit} remaining
              </span>
              {tickerQuota.used > 0 && (
                <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                  ({tickerQuota.used} used)
                </span>
              )}
            </div>
          </div>
        )}

        {/* Quota approaching / exhausted warning */}
        {auth.isAuthenticated && tickerQuota && (() => {
          const warning = getQuotaWarning(userTier, userRole, tickerQuota);
          if (!warning) return null;
          return (
            <div className="mt-3 mx-auto max-w-xl px-5 py-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg text-sm text-center">
              <p className="font-semibold text-yellow-900 dark:text-yellow-100 mb-1">Report limit alert</p>
              <p className="text-yellow-800 dark:text-yellow-200">{warning.message}</p>
              {warning.actions.length > 0 && (
                <div className="mt-2 flex flex-wrap justify-center gap-3">
                  {warning.actions.map((action) => (
                    <a
                      key={action.href}
                      href={action.href}
                      className="inline-block px-4 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-medium rounded-md transition-colors"
                    >
                      {action.text}
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
        
        {auth.isAuthenticated && quotaLoading && (
          <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
            Loading quota information...
          </div>
        )}

        <p className="text-lg text-gray-600 dark:text-gray-400">
          Enter a company ticker symbol to access the report
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label 
            htmlFor="ticker" 
            className="block text-sm font-medium mb-2"
          >
            Company Ticker
          </label>
          <input
            type="text"
            id="ticker"
            value={ticker}
            onChange={(e) => {
              setTicker(e.target.value);
              setError("");
            }}
            placeholder="e.g., AACG, TYG, GLPG"
            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            autoComplete="off"
            autoCapitalize="characters"
          />
          {error && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>

        {/* Report Format selector — commented out (PDF is the default)
        <div>
          <label className="block text-sm font-medium mb-2">
            Report Format
          </label>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => setFileFormat('pdf')}
              className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                fileFormat === 'pdf'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              PDF
            </button>
            <button
              type="button"
              onClick={() => setFileFormat('jpg')}
              className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                fileFormat === 'jpg'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              JPG
            </button>
          </div>
        </div>
        */}

        <button
          type="submit"
          disabled={loading || !auth.isAuthenticated}
          className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
        >
          {loading ? "Fetching Report..." : auth.isAuthenticated ? "Get Report" : "Please Log In"}
        </button>
      </form>

      <div className="mt-8 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h3 className="text-sm font-medium mb-2">Examples</h3>
        <div className="flex flex-wrap gap-2">
          {["AACG", "TYG", "DNUT", "GLPG", "BLIV"].map((example) => (
            <button
              key={example}
              type="button"
              onClick={() => setTicker(example)}
              className="px-3 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              {example}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
        <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-2">Important Notice</h3>
        <ul className="text-sm text-amber-800 dark:text-amber-200 space-y-1 list-disc list-inside">
          <li>Reports are available only for nano-, mini-, small-, and mid-cap companies.</li>
          <li>Only tickers representing operating businesses are available for reporting, not ETFs, funds, notes, or other financial instruments.</li>
          <li>If a ticker meets these criteria but is missing, please report it to us for review.</li>
          {(() => {
            const normalised = (userTier ?? "").toLowerCase().replace(/[^a-z]/g, "-");
            return ["scale", "enterprise"].includes(normalised) ? (
              <li>This portal report covers only the last four quarters. Full ticker history is available via API.</li>
            ) : null;
          })()}
        </ul>
      </div>

      {/* Display Image */}
      {reportType === 'image' && reportImage && (
        <div className="mt-8 p-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Report Image</h2>
            <button
              onClick={handleClear}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              Clear
            </button>
          </div>
          <img
            src={reportImage}
            alt={`Report for ${ticker}`}
            className="w-full h-auto rounded-lg shadow-lg"
          />
        </div>
      )}

      {/* Display PDF */}
      {reportType === 'pdf' && reportPdf && (
        <PdfViewer url={reportPdf} />
      )}
    </section>
  );
};